import React, { useState } from 'react';
import { User } from 'lucide-react';

const CommentItem = ({ comment, onReply, depth = 0 }) => {
    const [showReplyForm, setShowReplyForm] = useState(false);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleReplyClick = () => {
        setShowReplyForm(!showReplyForm);
    };

    const handleReplySubmit = (replyData) => {
        onReply(replyData, comment.id);
        setShowReplyForm(false);
    };

    return (
        <div className={`border-l-2 ${depth > 0 ? 'ml-8 mt-4' : 'mt-6'}`} style={{ borderLeftColor: '#C9A14F' }}>
            <div className="pl-4 pb-4">
                {/* Author and Date */}
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                            <p className="font-semibold text-sm" style={{ color: '#31415A' }}>
                                {comment.author_name}
                            </p>
                            <p className="text-xs text-gray-500">
                                {formatDate(comment.created_at)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Comment Content */}
                <p className="text-gray-700 mt-2 leading-relaxed">{comment.content}</p>

                {/* Reply Button - only show if depth < 2 to limit nesting */}
                {depth < 2 && (
                    <button
                        onClick={handleReplyClick}
                        className="text-sm mt-2 hover:underline"
                        style={{ color: '#C9A14F' }}
                    >
                        Reply
                    </button>
                )}

                {/* Reply Form */}
                {showReplyForm && onReply && (
                    <div className="mt-4 p-4 bg-gray-50 rounded">
                        <CommentReplyForm
                            onSubmit={handleReplySubmit}
                            onCancel={() => setShowReplyForm(false)}
                        />
                    </div>
                )}

                {/* Nested Replies */}
                {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-4">
                        {comment.replies.map((reply) => (
                            <CommentItem
                                key={reply.id}
                                comment={reply}
                                onReply={onReply}
                                depth={depth + 1}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Small reply form component
const CommentReplyForm = ({ onSubmit, onCancel }) => {
    const [content, setContent] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (content.trim().length < 10) {
            alert('Reply must be at least 10 characters');
            return;
        }
        onSubmit({ content });
        setContent('');
    };

    return (
        <form onSubmit={handleSubmit}>
            <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your reply..."
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{ '--tw-ring-color': '#C9A14F' }}
                rows="3"
                required
            />
            <div className="flex gap-2 mt-2">
                <button
                    type="submit"
                    className="px-4 py-2 rounded text-white text-sm"
                    style={{ backgroundColor: '#C9A14F' }}
                >
                    Submit Reply
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-100"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
};

export default CommentItem;
